import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import type Questionnaire from 'garaje/models/questionnaire';
import type QuestionnaireField from 'garaje/models/questionnaire-field';
import fixVideoUrl from 'garaje/utils/fix-video-url';
import type { LocaleKey } from 'garaje/utils/locale-options';
import { TrackedObject } from 'tracked-built-ins';
import type { Document, FileUpload } from 'utils/direct-uploader';

export interface PdfData {
  filename: string;
  id?: string | null;
  'pdf-url': string;
  'updated-at': Date;
}

export interface EmptyPdfData {
  filename: null;
  id?: string | null;
  'pdf-url': null;
  'updated-at': null;
}

function emptyPdfData(): EmptyPdfData {
  return {
    filename: null,
    'pdf-url': null,
    'updated-at': null,
  };
}

export type TranslatableAttribute = 'description' | 'pdf' | 'video-instructions' | 'video-url';

type QuestionnaireSectionTranslations = {
  [key in TranslatableAttribute]: {
    [key: LocaleKey]: string | PdfData | EmptyPdfData;
  };
};

class LocalizedQuestionnaireSection {
  #defaultLocale;
  #locale;
  #section;
  @tracked private _pendingPDFUpload: FileUpload | null = null;

  constructor(section: QuestionnaireSection, defaultLocale: LocaleKey, locale: LocaleKey) {
    this.#section = section;
    this.#defaultLocale = defaultLocale;
    this.#locale = locale;
  }

  get #isDefaultLocale(): boolean {
    return this.#locale === this.#defaultLocale;
  }

  get description(): string | null {
    if (this.#isDefaultLocale) return this.#section.description;
    return <string | null>this.#getTranslatedAttribute('description');
  }

  set description(description: string) {
    if (this.#isDefaultLocale) {
      this.#section.description = description;
      return;
    }

    this.#setTranslatedAttribute('description', description);
  }

  get document(): Document | null {
    const pdf = this.pdf;
    if (!pdf || !pdf.filename) return null;
    return {
      'document-url': pdf['pdf-url'],
      filename: pdf.filename,
      'updated-at': pdf['updated-at'],
    };
  }

  get pdf(): PdfData | EmptyPdfData {
    if (this.#isDefaultLocale) return this.#section.pdf;
    return <PdfData | EmptyPdfData | null>this.#getTranslatedAttribute('pdf') ?? emptyPdfData();
  }

  set pdf(pdf: PdfData | EmptyPdfData) {
    if (this.#isDefaultLocale) {
      this.#section.pdf = pdf;
      return;
    }

    this.#setTranslatedAttribute('pdf', pdf);
  }

  get pdfContent(): FileUpload | null {
    if (this.pendingPDFUpload) return this.pendingPDFUpload;

    if (!this.document || !this.document['document-url']) return null;

    return {
      document: this.document,
      isValid: true,
      // @ts-expect-error `file` here is a File "duck", not a real File
      file: { name: this.document.filename },
      reset: () => {},
    };
  }

  get pendingPDFUpload(): FileUpload | null {
    return this._pendingPDFUpload;
  }

  set pendingPDFUpload(upload: FileUpload | null) {
    this._pendingPDFUpload = upload;
  }

  get section(): QuestionnaireSection {
    return this.#section;
  }

  get videoInstructions(): string | null {
    if (this.#isDefaultLocale) return this.#section.videoInstructions;
    return <string | null>this.#getTranslatedAttribute('video-instructions');
  }

  set videoInstructions(instructions: string) {
    if (this.#isDefaultLocale) {
      this.#section.videoInstructions = instructions;
      return;
    }

    this.#setTranslatedAttribute('video-instructions', instructions);
  }

  get videoUrl(): string | null {
    if (this.#isDefaultLocale) return this.#section.videoUrl;
    return <string | null>this.#getTranslatedAttribute('video-url');
  }

  set videoUrl(url: string) {
    if (this.#isDefaultLocale) {
      this.#section.videoUrl = url;
      return;
    }

    this.#setTranslatedAttribute('video-url', url);
  }

  async uploadPDF(): Promise<void> {
    if (!this.pendingPDFUpload) return;

    const signedId = await this.pendingPDFUpload.upload();
    this.pdf = {
      ...emptyPdfData(),
      id: signedId,
    };

    this.pendingPDFUpload = null;
  }

  #getTranslatedAttribute(attr: TranslatableAttribute): string | PdfData | EmptyPdfData | null {
    if (!this.#section.customTranslations[attr]) return null;
    return this.#section.customTranslations[attr][this.#locale] ?? null;
  }

  #setTranslatedAttribute(attr: TranslatableAttribute, value: string | PdfData | EmptyPdfData): void {
    this.#section.customTranslations[attr] ||= new TrackedObject();
    this.#section.customTranslations[attr][this.#locale] = value;
  }
}
export type { LocalizedQuestionnaireSection };

export default class QuestionnaireSection extends Model {
  #localized = new Map<LocaleKey, LocalizedQuestionnaireSection>();

  @belongsTo('questionnaire', { async: false, inverse: 'questionnaireSections' })
  declare questionnaire: Questionnaire;

  @hasMany('questionnaire-field', { async: false, inverse: 'questionnaireSection' })
  declare questionnaireFields: QuestionnaireField[];

  @attr('boolean', { defaultValue: false }) declare allowSkipVideo: boolean;
  @attr('string') declare description: string;
  @attr('tracked-object', {
    defaultValue: () => new TrackedObject({ filename: null, 'pdf-url': null, 'updated-at': null }),
  })
  declare pdf: PdfData | EmptyPdfData;
  @attr('number') declare position: number;
  @attr('string') declare title: string;
  @attr('string') declare videoInstructions: string | null;
  @attr('video-url') declare videoUrl: string | null;

  @attr('deeply-tracked-object', { defaultValue: () => new TrackedObject() })
  declare customTranslations: QuestionnaireSectionTranslations;

  forLocale(locale: LocaleKey, defaultLocale: LocaleKey): LocalizedQuestionnaireSection {
    /* eslint-disable ember/use-ember-get-and-set */
    if (this.#localized.has(locale)) return <LocalizedQuestionnaireSection>this.#localized.get(locale);
    const localizedSection = new LocalizedQuestionnaireSection(this, defaultLocale, locale);
    this.#localized.set(locale, localizedSection);
    return localizedSection;
  }

  async save(adapterOptions?: object): Promise<this> {
    if (!this.isDeleted) {
      for (const localized of this.#localized.values()) {
        if (localized.pendingPDFUpload) {
          await localized.uploadPDF();
        }
        const videoUrl = localized.videoUrl;
        if (videoUrl) {
          localized.videoUrl = fixVideoUrl(videoUrl);
        }
      }
    }
    return super.save(adapterOptions);
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'questionnaire-section': QuestionnaireSection;
  }
}
